import type { DealFlowMatchData } from '@workspace/api-backend'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@workspace/ui/components/alert-dialog'
import { Textarea } from '@workspace/ui/components/textarea'
import { cn } from '@workspace/ui/lib/utils'
import { Check, ShieldCheck, ThumbsDown } from 'lucide-react'
import { useEffect, useState } from 'react'

const PASS_REASONS = [
  'Sector mismatch',
  'Stage too early',
  'Stage too late',
  'Geography mismatch',
  'Ticket size off',
  'Team concerns',
  'Market too small',
  'Other',
] as const

type PassReason = (typeof PASS_REASONS)[number]

interface PassDialogProps {
  startup: DealFlowMatchData
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (reasons: PassReason[], note: string) => void
}

export function PassDialog({ startup, open, onOpenChange, onConfirm }: PassDialogProps) {
  const [reasons, setReasons] = useState<Set<PassReason>>(() => new Set())
  const [note, setNote] = useState('')

  useEffect(() => {
    if (open) {
      setReasons(new Set())
      setNote('')
    }
  }, [open])

  const toggleReason = (r: PassReason) => {
    setReasons((prev) => {
      const next = new Set(prev)
      if (next.has(r)) next.delete(r)
      else next.add(r)
      return next
    })
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-xl">
        <AlertDialogHeader className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-lg border border-red-500/40 bg-red-500/10">
              <ThumbsDown className="size-5 text-red-400" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Remove from deal flow
              </p>
              <AlertDialogTitle className="truncate text-xl leading-tight">
                Pass on {startup.profile.startup.name}?
              </AlertDialogTitle>
            </div>
          </div>
          <AlertDialogDescription className="text-sm leading-relaxed text-muted-foreground">
            We'll hide this startup from your deal flow and use your feedback to refine future
            matches. You can always find them again in your archive.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3 py-1">
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-foreground">
              Why are you passing?
            </p>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70">
              Optional · pick any that apply
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {PASS_REASONS.map((r) => {
              const selected = reasons.has(r)
              return (
                <button
                  key={r}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => toggleReason(r)}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                    selected
                      ? 'border-red-500/60 bg-red-500/20 text-red-200'
                      : 'border-border bg-card/60 text-foreground/80 hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-200'
                  )}
                >
                  {selected && <Check className="size-3" />}
                  {r}
                </button>
              )
            })}
          </div>
        </div>

        <div className="space-y-2 py-1">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/80">
            Anything else? (optional)
          </p>
          <Textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="A specific concern, what would change your mind, anything that helps us match you better…"
            className="resize-none"
          />
        </div>

        <div className="flex items-start gap-2 rounded-lg border border-border/60 bg-muted/30 px-3.5 py-2.5 text-xs text-muted-foreground">
          <ShieldCheck className="mt-0.5 size-3.5 shrink-0 text-emerald-400" />
          <p className="leading-relaxed">
            Your feedback is <span className="font-semibold text-foreground/90">anonymized</span>,
            the founder never sees who passed or why. We only use it to improve match relevance.
          </p>
        </div>

        <AlertDialogFooter className="mt-2">
          <AlertDialogCancel className="sm:mr-auto">Keep in deal flow</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => onConfirm(Array.from(reasons), note.trim())}
            className="border-red-500/50 bg-red-500/15 text-red-400 hover:bg-red-500/25 hover:text-red-300"
          >
            Pass on {startup.profile.startup.name}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
